import { isAgentsEnabled } from "@hightouch/lib/llm/agents-tier";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useSpaceContext } from "src/components/spaces";
import { useAiFeatureSettings } from "src/hooks/use-ai-feature-settings";
import { useEntitlements } from "src/hooks/use-entitlement";
import { useAgentsTier } from "src/pages/agents/hooks/use-agents-tier";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { PROJECTS_PATHS } from "src/pages/projects/constants";

/** A workspace with agents but not audiences, for which Customer Studio narrows to Schema. `isLoading` holds until entitlements load. */
export const useIsAgentsOnlyWorkspace = () => {
  const { data, isLoading } = useEntitlements(false);
  const agentsTier = useAgentsTier();
  return {
    isAgentsOnly: isAgentsEnabled(agentsTier) && !data.entitlements.audiences,
    isLoading,
  };
};

/** Agent chats, which lead the AMP nav as its Home. */
export const AGENT_CHATS_PATH = "/agents/chats";

/** Whether agent pages show in the nav at all, in the CDP nav or, with the product tabs, the AMP nav and settings. */
export const useShowAgentsInNav = () => {
  const { appAgentChatsInSpaces } = useFlags();
  const { agents: agentsFeatureEnabled } = useAiFeatureSettings();
  const { isInSpace, isPathAccessibleInSpace } = useSpaceContext();
  const { isPermitted: canViewAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_view_agents" },
  });

  // Show Agents in Space views only when the rollout and route access are enabled.
  if (
    isInSpace &&
    (appAgentChatsInSpaces !== true || !isPathAccessibleInSpace("/agents"))
  ) {
    return false;
  }

  // Hidden without permission to view agents, or when the org has turned the Agents feature off.
  return canViewAgents && agentsFeatureEnabled;
};

/** AMP's home: agent chats, or the projects list when the agent pages are hidden from this viewer. */
export const useAmpHomeHref = () => {
  const showAgents = useShowAgentsInNav();
  const isAgentsEnabled = useAgentsEnabledFlag();
  return showAgents && isAgentsEnabled
    ? AGENT_CHATS_PATH
    : PROJECTS_PATHS.projects;
};
